import React, { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import { PrintDocumentTitle, PrintInfoGrid } from '@/components/shared/print';

export interface MaterialStagingPrintData {
  docNo: string;
  docDate: Date | string;
  poCode: string;
  productName: string;
  customerName?: string;
  materialCode: string;
  materialName: string;
  unit: string;
  targetStepName?: string;
  warehouseName?: string;
  notes?: string;
  lots: Array<{
    lotNumber: string;
    quantity: number;
    locationCode?: string;
    expiryDate?: string | Date;
  }>;
  totalQuantity: number;
  issuedBy?: string;
}

interface MaterialStagingPrintTemplateProps {
  data: MaterialStagingPrintData;
  mode?: 'DOCUMENT' | 'PALLET_DECAL';
}

export const MaterialStagingPrintTemplate = forwardRef<HTMLDivElement, MaterialStagingPrintTemplateProps>(
  ({ data, mode = 'DOCUMENT' }, ref) => {
    const formattedDate = data.docDate ? format(new Date(data.docDate), 'dd/MM/yyyy HH:mm') : format(new Date(), 'dd/MM/yyyy HH:mm');

    if (mode === 'PALLET_DECAL') {
      return (
        <div ref={ref} className="space-y-6 print:space-y-0">
          {data.lots.map((lot, idx) => {
            const qrPayload = JSON.stringify({
              doc: data.docNo,
              po: data.poCode,
              mat: data.materialCode,
              qty: lot.quantity,
            });

            return (
              <div
                key={lot.lotNumber || idx}
                className="pallet-decal-card bg-white p-6 border-4 border-black rounded-lg w-[580px] max-w-full text-black font-sans mx-auto print:break-after-page print:border-4 print:w-full print:m-0"
              >
                {/* Header */}
                <div className="border-b-2 border-black pb-3 text-center">
                  <div className="text-xs font-bold tracking-widest text-slate-700 uppercase">
                    CÔNG TY CỔ PHẦN BAO BÌ APV • XƯỞNG SẢN XUẤT
                  </div>
                  <h1 className="text-2xl font-black uppercase tracking-tight mt-0.5">
                    THẺ PALLET CẤP PHÁT VẬT TƯ
                  </h1>
                  <div className="flex justify-between items-center text-xs font-semibold mt-1 px-1 text-slate-600">
                    <span>Số phiếu: <strong className="text-black font-mono">{data.docNo}</strong></span>
                    <span>Ngày cấp: <strong className="text-black">{formattedDate}</strong></span>
                  </div>
                </div>

                {/* Body Content */}
                <div className="grid grid-cols-3 gap-4 py-4 border-b-2 border-black">
                  <div className="col-span-2 space-y-2 text-sm">
                    <div>
                      <span className="text-xs text-slate-500 uppercase font-bold block">Lệnh sản xuất (LSX):</span>
                      <span className="text-lg font-black font-mono text-indigo-900">{data.poCode}</span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500 uppercase font-bold block">Sản phẩm:</span>
                      <span className="font-bold text-slate-900 line-clamp-1">{data.productName}</span>
                      {data.customerName && (
                        <span className="text-xs text-slate-600 block">KH: {data.customerName}</span>
                      )}
                    </div>
                    <div className="pt-1 border-t border-dashed border-slate-300">
                      <span className="text-xs text-slate-500 uppercase font-bold block">Vật tư cấp phát:</span>
                      <span className="font-black text-base text-slate-900">[{data.materialCode}] {data.materialName}</span>
                    </div>
                    <div className="flex gap-4 text-xs font-semibold pt-1">
                      <div>
                        <span className="text-slate-500 block">Công đoạn nhận:</span>
                        <span className="font-bold text-black uppercase">{data.targetStepName || 'In Offset'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Kho xuất:</span>
                        <span className="font-bold text-black">{data.warehouseName || 'Kho Vật Tư'}</span>
                      </div>
                    </div>
                  </div>

                  {/* QR Code & Lot Badge */}
                  <div className="flex flex-col items-center justify-center p-2 bg-slate-50 border border-slate-300 rounded">
                    <QRCodeSVG value={qrPayload} size={115} level="M" />
                    <span className="text-[10px] font-mono text-slate-600 mt-1.5 uppercase font-bold text-center">
                      Quét nhận vật tư
                    </span>
                  </div>
                </div>

                {/* Big Lot & Quantity Ribbon */}
                <div className="py-4 bg-amber-50/70 -mx-6 px-6 border-b-2 border-black flex justify-between items-center">
                  <div>
                    <span className="text-xs font-bold text-amber-800 uppercase block">VẬT TƯ:</span>
                    <span className="text-2xl font-black font-mono text-amber-950 tracking-wide">{data.materialCode}</span>
                    {(lot.locationCode || data.warehouseName) && (
                      <span className="text-xs text-slate-600 block font-medium">Kho: {lot.locationCode || data.warehouseName}</span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-slate-600 uppercase block">SỐ LƯỢNG TRÊN PALLET:</span>
                    <span className="text-4xl font-black font-mono text-red-600 tracking-tight">
                      {lot.quantity.toLocaleString('vi-VN')}
                    </span>
                    <span className="text-sm font-bold text-slate-800 ml-1.5 uppercase">{data.unit}</span>
                  </div>
                </div>

                {/* Footer Signatures */}
                <div className="pt-3 flex justify-between text-xs text-slate-600 text-center">
                  <div className="w-1/3">
                    <div className="font-bold text-black uppercase">Thủ Kho Cấp</div>
                    <div className="h-10"></div>
                    <div className="text-[11px] text-slate-500 font-medium">({data.issuedBy || 'Ký, họ tên'})</div>
                  </div>
                  <div className="w-1/3 border-x border-slate-200">
                    <div className="font-bold text-black uppercase">Vận Chuyển Xưởng</div>
                    <div className="h-10"></div>
                    <div className="text-[11px] text-slate-500 font-medium">(Ký, họ tên)</div>
                  </div>
                  <div className="w-1/3">
                    <div className="font-bold text-black uppercase">Thợ Máy Nhận</div>
                    <div className="h-10"></div>
                    <div className="text-[11px] text-slate-500 font-medium">(Ký, họ tên)</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Default: A4 Document Mode (Phiếu xuất cấp phát kho ra xưởng)
    return (
      <div
        ref={ref}
        className="bg-white p-8 text-black font-sans max-w-[820px] mx-auto border border-slate-200 shadow-sm print:shadow-none print:border-none print:p-4 print:max-w-none"
      >
        {/* Header Standard ISO */}
        <div className="flex justify-between items-start border-b-2 border-black pb-4">
          <div>
            <h2 className="text-base font-bold uppercase tracking-wider text-slate-800">
              CÔNG TY CỔ PHẦN BAO BÌ APV
            </h2>
            <p className="text-xs text-slate-600 mt-0.5">
              Địa chỉ: Lô CN-08, KCN Yên Phong, Bắc Ninh • Hotline: 024.3999.8888
            </p>
          </div>
          <div className="text-right text-xs">
            <div className="text-slate-500 mt-0.5">Số phiếu: <strong className="text-black font-mono">{data.docNo}</strong></div>
            <div className="text-slate-500">Ngày xuất: {formattedDate}</div>
          </div>
        </div>

        {/* Title */}
        <PrintDocumentTitle
          title="PHIẾU CẤP PHÁT VẬT TƯ"
          subtitle="MATERIAL ISSUE NOTE"
          docNo={data.docNo}
          docNoLabel="Số Phiếu"
          date={formattedDate}
          dateLabel="Ngày Xuất"
          className="my-4"
        />

        {/* Metadata Grid */}
        <PrintInfoGrid
          labelWidth="w-[145px]"
          leftColumn={[
            { label: 'Lệnh sản xuất (LSX)', labelEn: 'PO Code', value: data.poCode, valueClassName: 'font-black font-mono text-indigo-900' },
            { label: 'Tên sản phẩm', labelEn: 'Product', value: data.productName, valueClassName: 'font-semibold text-slate-900' },
            { label: 'Khách hàng', labelEn: 'Customer', value: data.customerName || 'Nội bộ' },
          ]}
          rightColumn={[
            { label: 'Kho xuất cấp', labelEn: 'Warehouse', value: data.warehouseName || 'Kho Vật Tư', valueClassName: 'font-bold text-slate-900' },
            { label: 'Công đoạn nhận', labelEn: 'Target Step', value: data.targetStepName || 'In Offset', valueClassName: 'font-bold text-indigo-700 uppercase' },
            { label: 'Ghi chú cấp phát', labelEn: 'Notes', value: data.notes || 'Cấp theo định mức KHSX', valueClassName: 'italic text-slate-800' },
          ]}
          className="mb-6"
        />

        {/* Lots Breakdown Table */}
        <table className="w-full text-xs border-collapse border border-black mb-6">
          <thead>
            <tr className="bg-slate-100 text-slate-800 uppercase font-bold text-center">
              <th className="border border-black px-2 py-2 w-10">STT</th>
              <th className="border border-black px-3 py-2 text-left">Mã Vật Tư</th>
              <th className="border border-black px-3 py-2 text-left">Tên Chi Tiết / Quy Cách</th>
              <th className="border border-black px-2 py-2 w-16">ĐVT</th>
              <th className="border border-black px-3 py-2">Kho</th>
              <th className="border border-black px-3 py-2 text-right w-24">Số Lượng</th>
              <th className="border border-black px-3 py-2 w-24">Ghi Chú</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black">
            {data.lots.map((lot, idx) => (
              <tr key={lot.lotNumber || idx} className="text-center font-medium">
                <td className="border border-black px-2 py-2">{idx + 1}</td>
                <td className="border border-black px-3 py-2 text-left font-mono font-bold text-indigo-900 whitespace-nowrap">
                  {data.materialCode}
                </td>
                <td className="border border-black px-3 py-2 text-left font-semibold">
                  {data.materialName}
                </td>
                <td className="border border-black px-2 py-2 uppercase whitespace-nowrap">{data.unit}</td>
                <td className="border border-black px-3 py-2 text-slate-600 whitespace-nowrap">
                  {lot.locationCode || data.warehouseName || ''}
                </td>
                <td className="border border-black px-3 py-2 text-right font-mono font-bold text-red-600 text-sm whitespace-nowrap">
                  {lot.quantity.toLocaleString('vi-VN')}
                </td>
                <td className="border border-black px-3 py-2 text-slate-500 italic text-[11px]" />
              </tr>
            ))}
            <tr className="bg-slate-50 font-bold">
              <td colSpan={5} className="border border-black px-3 py-2 text-right uppercase">
                Tổng Cộng Số Lượng Cấp Sàn Xưởng:
              </td>
              <td className="border border-black px-3 py-2 text-right font-mono text-base text-red-600">
                {data.totalQuantity.toLocaleString('vi-VN')}
              </td>
              <td className="border border-black px-2 py-2 text-center uppercase font-bold text-slate-800">
                {data.unit}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Commitment Statement */}
        <div className="text-xs text-slate-600 italic mb-8">
          * Ghi chú: Vật tư xuất kho đã được KCS/Thủ kho kiểm tra ngoại quan nguyên vẹn và số lượng. 
          Thợ máy khi nhận pallet tại chân máy cần kiểm tra số lượng, quét mã QR trên thẻ pallet để xác nhận và chịu trách nhiệm bảo quản trong ca.
        </div>

        {/* Signature Blocks */}
        <div className="grid grid-cols-4 gap-4 text-center text-xs">
          <div>
            <div className="font-bold uppercase text-slate-900">Người Lập Phiếu</div>
            <div className="text-[11px] text-slate-500 italic">(Ký, họ tên)</div>
            <div className="h-16"></div>
            <div className="font-semibold text-slate-800">{data.issuedBy || 'Thủ kho'}</div>
          </div>
          <div>
            <div className="font-bold uppercase text-slate-900">Thủ Kho Xuất</div>
            <div className="text-[11px] text-slate-500 italic">(Ký, họ tên)</div>
            <div className="h-16"></div>
            <div className="font-semibold text-slate-800">Thủ kho cấp</div>
          </div>
          <div>
            <div className="font-bold uppercase text-slate-900">Vận Chuyển Xưởng</div>
            <div className="text-[11px] text-slate-500 italic">(Ký, họ tên)</div>
            <div className="h-16"></div>
            <div className="font-semibold text-slate-800">Giao nhận</div>
          </div>
          <div>
            <div className="font-bold uppercase text-slate-900">Thợ Máy / Quản Đốc</div>
            <div className="text-[11px] text-slate-500 italic">(Ký, xác nhận)</div>
            <div className="h-16"></div>
            <div className="font-semibold text-slate-800">Nhận vật tư</div>
          </div>
        </div>
      </div>
    );
  }
);

MaterialStagingPrintTemplate.displayName = 'MaterialStagingPrintTemplate';
